import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/progress-bar.default.js';

/** @type {readonly ['sm', 'md', 'lg']} */
export const PROGRESS_BAR_SIZES = Object.freeze(['sm', 'md', 'lg']);

/** @type {readonly ['primary', 'success', 'warning', 'danger']} */
export const PROGRESS_BAR_INTENTS = Object.freeze(['primary', 'success', 'warning', 'danger']);

/** @type {readonly ['idle-zero', 'determinate', 'complete', 'indeterminate']} */
export const PROGRESS_BAR_MODES = Object.freeze(['idle-zero', 'determinate', 'complete', 'indeterminate']);

class RunProgressBar extends skinResolutionMixin(RunElement) {
  // null = no value provided → indeterminate rendering (contract: value is
  // `number | null`, default null; NaN / non-finite coerce to null too).
  #value = null;
  #min = 0;
  #max = 100;
  #indeterminate = false;
  #size = 'md';
  #intent = 'primary';
  #label = null;
  #showValue = false;
  #warnedRange = false;

  // Starts true so initial mount at max does not trigger a rising-edge event.
  _wasComplete = true;

  // Default skin is always bundled — zero-async first paint, no flash.
  static defaultSkin = defaultSkin;

  // Every non-default skin is lazy. The default skin is NOT listed here.
  static skinLoaders = {};

  static get observedAttributes() {
    return ['value', 'min', 'max', 'indeterminate', 'size', 'intent', 'label', 'show-value'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'progressbar');
    this.#updateState();
    this.requestUpdate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'value':
        this.value = newValue === null ? null : Number(newValue);
        break;
      case 'min':
        this.min = newValue === null ? 0 : Number(newValue);
        break;
      case 'max':
        this.max = newValue === null ? 100 : Number(newValue);
        break;
      case 'indeterminate':
        this.indeterminate = newValue !== null;
        break;
      case 'size':
        this.size = newValue === null ? 'md' : newValue;
        break;
      case 'intent':
        this.intent = newValue === null ? 'primary' : newValue;
        break;
      case 'label':
        this.label = newValue;
        break;
      case 'show-value':
        this.showValue = newValue !== null;
        break;
    }
  }

  // Clamped value shared with skins for fill-width and percentage display.
  // Falls back to min while indeterminate (null value).
  get _coercedValue() { return this.#value ?? this.#min; }

  get value() {
    return this.#value;
  }

  set value(raw) {
    let next;
    if (raw === null || raw === undefined) {
      next = null;
    } else {
      const n = Number(raw);
      next = Number.isFinite(n) ? Math.min(this.#max, Math.max(this.#min, n)) : null;
    }
    if (this.#value === next) return;
    this.#value = next;
    if (next === null) {
      // Remove rather than write the string "null" — absence IS the signal.
      this.removeAttribute('value');
    } else {
      this.setAttribute('value', String(next));
    }
    this.#updateState();
    this.#checkComplete();
    this.requestUpdate();
  }

  get min() {
    return this.#min;
  }

  set min(raw) {
    const coerced = Number(raw);
    const next = Number.isFinite(coerced) ? coerced : 0;
    if (this.#min === next) return;
    this.#min = next;
    this.setAttribute('min', String(next));
    this.#validateRange();
    this.#reclampValue();
    this.#updateState();
    this.#checkComplete();
    this.requestUpdate();
  }

  get max() {
    return this.#max;
  }

  set max(raw) {
    const coerced = Number(raw);
    const next = Number.isFinite(coerced) ? coerced : 100;
    if (this.#max === next) return;
    this.#max = next;
    this.setAttribute('max', String(next));
    this.#validateRange();
    this.#reclampValue();
    this.#updateState();
    this.#checkComplete();
    this.requestUpdate();
  }

  get indeterminate() {
    return this.#indeterminate;
  }

  set indeterminate(raw) {
    const coerced = Boolean(raw);
    if (this.#indeterminate === coerced) return;
    this.#indeterminate = coerced;
    this.toggleAttribute('indeterminate', coerced);
    this.#updateState();
    this.#checkComplete();
    this.requestUpdate();
  }

  get size() {
    return this.#size;
  }

  set size(raw) {
    const coerced = PROGRESS_BAR_SIZES.includes(String(raw).trim())
      ? String(raw).trim()
      : 'md';
    if (this.#size === coerced) {
      // Value unchanged but the attribute may never have been reflected
      // (assigning the default to a fresh instance) — keep them in sync.
      if (this.getAttribute('size') !== coerced) this.setAttribute('size', coerced);
      return;
    }
    this.#size = coerced;
    this.setAttribute('size', coerced);
    this.requestUpdate();
  }

  get intent() {
    return this.#intent;
  }

  set intent(raw) {
    const coerced = PROGRESS_BAR_INTENTS.includes(String(raw).trim())
      ? String(raw).trim()
      : 'primary';
    if (this.#intent === coerced) {
      if (this.getAttribute('intent') !== coerced) this.setAttribute('intent', coerced);
      return;
    }
    this.#intent = coerced;
    this.setAttribute('intent', coerced);
    this.requestUpdate();
  }

  get label() {
    return this.#label;
  }

  set label(raw) {
    const coerced = raw == null ? null : (String(raw).trim() || null);
    if (this.#label === coerced) return;
    this.#label = coerced;
    if (coerced) {
      this.setAttribute('label', coerced);
    } else {
      this.removeAttribute('label');
    }
    this.#updateState();
    this.requestUpdate();
  }

  get showValue() {
    return this.#showValue;
  }

  set showValue(raw) {
    const coerced = Boolean(raw);
    if (this.#showValue === coerced) return;
    this.#showValue = coerced;
    this.toggleAttribute('show-value', coerced);
    this.requestUpdate();
  }

  // updated() is present because ARIA state must be refreshed after the skin
  // has rendered and the host's attribute surface is fully stable, and the
  // headless base owns the --run-progress-fill-width write on the fill part.
  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateState();
    this.#writeFillWidth();
  }

  // Resolved rendering mode (also written to [data-mode] for skin CSS).
  get _mode() {
    if (this.#indeterminate || this.#value === null) return 'indeterminate';
    if (this.#value >= this.#max) return 'complete';
    if (this.#value <= this.#min) return 'idle-zero';
    return 'determinate';
  }

  // min > max is contract-invalid: fall back to the 0/100 defaults and warn
  // once per instance (repeat violations stay silent).
  #validateRange() {
    if (this.#min <= this.#max) return;
    if (!this.#warnedRange) {
      this.#warnedRange = true;
      console.warn(
        `[run-progress-bar] min (${this.#min}) > max (${this.#max}); ` +
        'falling back to min=0, max=100.'
      );
    }
    this.#min = 0;
    this.#max = 100;
    this.setAttribute('min', '0');
    this.setAttribute('max', '100');
  }

  #reclampValue() {
    if (this.#value === null) return;
    const clamped = Math.min(this.#max, Math.max(this.#min, this.#value));
    if (clamped === this.#value) return;
    this.#value = clamped;
    this.setAttribute('value', String(clamped));
  }

  // Atomic state write: [data-mode], [data-complete], and the full ARIA
  // surface change in the same synchronous pass — no interleaved frame may
  // observe a partially-swapped state (aria-valuenow present + aria-busy).
  #updateState() {
    const mode = this._mode;
    this.setAttribute('data-mode', mode);
    this.toggleAttribute('data-complete', mode === 'complete');

    if (mode === 'indeterminate') {
      // aria-valuenow MUST be absent (not emptied) — AT reads absence as
      // the signal to announce "in progress" rather than a numeric value.
      this.removeAttribute('aria-valuenow');
      this.setAttribute('aria-busy', 'true');
    } else {
      this.setAttribute('aria-valuenow', String(this.#value));
      this.removeAttribute('aria-busy');
    }

    this.setAttribute('aria-valuemin', String(this.#min));
    this.setAttribute('aria-valuemax', String(this.#max));

    if (this.#label) {
      this.setAttribute('aria-label', this.#label);
    } else {
      this.removeAttribute('aria-label');
    }
  }

  // The headless base owns --run-progress-fill-width; skins consume it via
  // inline-size: var(--run-progress-fill-width, 0%) on the fill part.
  #writeFillWidth() {
    const fill = this.shadowRoot?.querySelector('[part="fill"]');
    if (!fill) return;
    const range = this.#max - this.#min;
    const pct = (this._mode === 'indeterminate' || range <= 0)
      ? 0
      : Math.min(100, Math.max(0, ((this.#value - this.#min) / range) * 100));
    fill.style.setProperty('--run-progress-fill-width', `${pct}%`);
  }

  // Rising-edge detector: fires run-progress-complete on the first transition
  // from incomplete to complete. Resets when dropping below max or entering
  // indeterminate mode so a subsequent return to max fires again.
  #checkComplete() {
    const isComplete = this._mode === 'complete';
    if (isComplete && !this._wasComplete) {
      this._wasComplete = true;
      this.dispatchEvent(new CustomEvent('run-progress-complete', {
        detail: { value: this.#value, max: this.#max },
        bubbles: true,
        composed: true,
      }));
    } else if (!isComplete) {
      this._wasComplete = false;
    }
  }
}

if (!customElements.get('run-progress-bar')) {
  customElements.define('run-progress-bar', RunProgressBar);
}

export { RunProgressBar };
export default RunProgressBar;
